import type { ReactNode } from 'react';
import { ArrowRight, ArrowUpRight, Eye, Plus } from 'lucide-react';
import { useLocale } from '../../../lib/locale-context';
import { useTranslation } from '../../../lib/i18n';
import type { PresenceAction } from '../../../lib/presence-checklist';
import type { SpaceCard as SpaceCardModel, SpaceCardAction, SpaceCardChip, SpaceCardChips, SpaceCardRow } from '../../../lib/space-card';
import { Button, Card, TextButton, Typography } from '../ui';
import { PresenceActionButton, usePresenceCopy } from './PresenceChecklist';
import { PresenceGlyph } from './PresenceGlyph';
import { formatCompactCount, formatCount, TrendChip, useTrendWords } from './SiteFigures';
import { Sparkline } from './Sparkline';
import styles from './SpaceCard.module.css';

// The Space card (Space rules spec 2026-10-29, behind `symphonySpaceCardV2`; the prototype's `.sp-card`, as revised
// 2026-11-03): it replaces the site strip when the toggle is on. Every decision is `spaceCard` in lib/space-card.ts
// (shared vectors); this file only draws it, as the prototype lays it out:
//   - chips across the top of a social-only card: the networks' names, text only, the shown profile's pressed — a chip
//     PICKS the profile the card shows; "+N" and "+ Add" open Manage Online Presence. Any other card carries the chips as
//     its "Connected accounts" row, each with its mark, handle and followers, opening the profile;
//   - left (5 parts): the badge (a social card: the profile's network mark on the accent tint), the name and address,
//     "View Site" / "Preview" as a quiet eye link beside them; then the dark "Manage Online Presence" button and the
//     equal OUTLINED buttons (no ⋯ on the card);
//   - right (7 parts): the tinted Visits strip (the strongest thing on the card), then quiet rows — an uppercase label,
//     a grey value and an underlined link, or the card's ONE green button; WordPress's move is a block, not a row;
//   - footer: one quiet line under a divider, "Expand your online reach: <Connect …>  →", opening Connectors.
// Narrow (< 720): one column, the footer last.

/** The em dash a Visits read that failed shows: a figure, not a word, so it is not a catalog string. */
const UNKNOWN = '—';

/**
 * The card's own targets (Connectors, Apps, Calendar, Manage) are never a url or desktop-only, so the presence
 * button draws them through its plain onClick branch; only `onAct` needs the card's wider action type.
 */
function asPresence(action: SpaceCardAction): PresenceAction {
  return action as PresenceAction;
}

/** An outlined button on the left (Edit Site ↗, Open in Shopify ↗), or a row's green call to action. */
function CardButton({ action, onAct, testId }: { action: SpaceCardAction; onAct: (a: SpaceCardAction) => void; testId: string }) {
  return (
    <PresenceActionButton
      action={asPresence(action)}
      onAct={() => onAct(action)}
      variant={action.primary ? 'contained' : 'outlined'}
      color={action.primary ? 'accent' : 'standard'}
      size={action.primary ? 'small' : 'medium'}
      className={action.primary ? undefined : styles.button}
      testId={testId}
      endIcon={action.target.kind === 'url' && !action.primary ? <ArrowUpRight size={14} aria-hidden className="dir-flip" /> : undefined}
    />
  );
}

/** "View Site" / "Preview" beside the name: a ghost link with the eye, opening the site in a new tab. */
function ViewLink({ action, onAct }: { action: SpaceCardAction; onAct: (a: SpaceCardAction) => void }) {
  const copy = usePresenceCopy();
  if (action.target.kind !== 'url') return null;
  return (
    <Button
      variant="text"
      size="small"
      href={action.target.url}
      external
      target="_blank"
      rel="noopener noreferrer"
      startIcon={<Eye size={16} aria-hidden />}
      className={styles.view}
      data-testid={`spaceCardView-${action.bi}`}
      onClick={() => onAct(action)}
    >
      {copy(action.label)}
    </Button>
  );
}

/** A row's quiet link: underlined, in the primary ink. Row links are never desktop-only. */
function RowLink({ action, onAct, testId }: { action: SpaceCardAction; onAct: (a: SpaceCardAction) => void; testId: string }) {
  const copy = usePresenceCopy();
  const label = copy(action.label);
  return action.target.kind === 'url' ? (
    <TextButton
      size="small"
      underline
      href={action.target.url}
      external
      target="_blank"
      rel="noopener noreferrer"
      className={styles.link}
      data-testid={testId}
      onClick={() => onAct(action)}
    >
      {label}
    </TextButton>
  ) : (
    <TextButton size="small" underline className={styles.link} data-testid={testId} onClick={() => onAct(action)}>
      {label}
    </TextButton>
  );
}

/** What a card does with a chip, "+N" and "+ Add". */
interface ChipHandlers {
  /** A "Connected accounts" chip: its profile was opened (or, with no public page, Manage). */
  onChip: (chip: SpaceCardChip) => void;
  /** A chip across a social card: show that profile. */
  onSelect: (chip: SpaceCardChip) => void;
  onMore: () => void;
  onAdd: () => void;
}

/** The chips across the top of a social card: the networks' names, the shown profile's pressed; a chip picks it. */
function TopChips({ chips, onSelect, onMore, onAdd }: { chips: SpaceCardChips } & ChipHandlers) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  return (
    <div className={styles.chipRow} data-testid="spaceCardChips">
      {chips.shown.map((chip) => {
        const pressed = chip.id === chips.selected;
        return (
          <Button
            key={chip.id}
            variant="outlined"
            size="small"
            aria-pressed={pressed}
            className={`${styles.chip}${pressed ? ` ${styles.chipPressed}` : ''}`}
            data-testid={`spaceCardChip-${chip.id}`}
            onClick={() => onSelect(chip)}
          >
            {chip.network || chip.name}
          </Button>
        );
      })}
      {chips.more > 0 && (
        <Button variant="outlined" size="small" className={styles.chip} data-testid="spaceCardChipsMore" onClick={onMore}>
          +{formatCount(chips.more, locale)}
        </Button>
      )}
      <Button variant="outlined" size="small" className={styles.chip} startIcon={<Plus size={14} aria-hidden />} data-testid="spaceCardChipsAdd" onClick={onAdd}>
        {t('spaceCard.action.addAccount')}
      </Button>
    </div>
  );
}

/** The "Connected accounts" row's chips: mark, handle and followers ("@studio · 1.2K"); a chip opens its profile. */
function AccountChips({ chips, onChip, onMore, onAdd }: { chips: SpaceCardChips } & ChipHandlers) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const chipLabel = (chip: SpaceCardChip) => (chip.followers !== null ? `${chip.name} · ${formatCompactCount(chip.followers, locale)}` : chip.name);
  return (
    <div className={styles.chipsInline} data-testid="spaceCardAccounts">
      {chips.shown.map((chip) =>
        chip.url ? (
          <Button
            key={chip.id}
            variant="outlined"
            size="small"
            href={chip.url}
            external
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<PresenceGlyph glyph={chip.glyph} size={14} />}
            className={styles.chip}
            data-testid={`spaceCardChip-${chip.id}`}
            onClick={() => onChip(chip)}
          >
            {chipLabel(chip)}
          </Button>
        ) : (
          <Button
            key={chip.id}
            variant="outlined"
            size="small"
            startIcon={<PresenceGlyph glyph={chip.glyph} size={14} />}
            className={styles.chip}
            data-testid={`spaceCardChip-${chip.id}`}
            onClick={() => onChip(chip)}
          >
            {chipLabel(chip)}
          </Button>
        ),
      )}
      {chips.more > 0 && (
        <Button variant="outlined" size="small" className={styles.chip} data-testid="spaceCardChipsMore" onClick={onMore}>
          +{formatCount(chips.more, locale)}
        </Button>
      )}
      <Button variant="outlined" size="small" className={styles.chip} startIcon={<Plus size={14} aria-hidden />} data-testid="spaceCardChipsAdd" onClick={onAdd}>
        {t('spaceCard.action.addAccount')}
      </Button>
    </div>
  );
}

function RowLabel({ children }: { children: ReactNode }) {
  return <span className={styles.key}>{children}</span>;
}

/** The tinted Visits strip: label + big number (+ trend), the small line, and Analytics — one row. */
function VisitsStrip({ row, onAct }: { row: SpaceCardRow; onAct: (a: SpaceCardAction) => void }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const words = useTrendWords();
  const copy = usePresenceCopy();
  const visits = row.visits;
  if (!visits) return null;
  const label = copy(row.label);
  const figure = visits.state === 'known' ? formatCount(visits.count, locale) : visits.state === 'unknown' ? UNKNOWN : null;
  return (
    <Card variant="stone" size="sm" className={styles.visits} data-testid="spaceCardVisits" data-state={visits.state}>
      <span className={styles.visitsText}>
        <RowLabel>{label}</RowLabel>
        {figure !== null ? (
          <span className={styles.visitsFigure} aria-label={[label, figure, words(visits.trend)].filter(Boolean).join(', ')}>
            <span className={styles.visitsNumber}>{figure}</span>
            {visits.trend && <TrendChip trend={visits.trend} />}
          </span>
        ) : (
          <Typography variant="p2" color="primary" component="span">
            {t('spaceCard.visits.publishFirst')}
          </Typography>
        )}
      </span>
      {figure !== null && visits.daily.length > 0 && <Sparkline values={visits.daily} width={90} height={22} className={styles.spark} />}
      {row.action && (
        <Button variant="outlined" size="small" className={styles.visitsAction} data-testid="spaceCardAnalytics" onClick={() => row.action && onAct(row.action)}>
          {copy(row.action.label)}
        </Button>
      )}
    </Card>
  );
}

/** WordPress's move to Symphony (the prototype's `.sp-mig`): why, in one line, then the green button. No label. */
function MigrateBlock({ row, onAct }: { row: SpaceCardRow; onAct: (a: SpaceCardAction) => void }) {
  const copy = usePresenceCopy();
  return (
    <div className={styles.migrate} data-testid={`spaceCardRow-${row.kind}`}>
      {row.value && !('count' in row.value) && !('unknown' in row.value) && (
        <Typography variant="p2" color="secondary" component="p" className={styles.migrateText}>
          {copy(row.value)}
        </Typography>
      )}
      {row.action && <CardButton action={row.action} onAct={onAct} testId="spaceCardPrimary" />}
    </div>
  );
}

/**
 * A quiet row: the uppercase label, then the grey value (a count in the primary ink) and the link (or the green
 * button) on the end — or, for "Connected accounts", the chips.
 */
function QuietRow({ row, onAct, chipHandlers }: { row: SpaceCardRow; onAct: (a: SpaceCardAction) => void; chipHandlers: ChipHandlers }) {
  const { t } = useTranslation();
  const copy = usePresenceCopy();
  const { locale } = useLocale();
  return (
    <div className={styles.row} data-testid={`spaceCardRow-${row.kind}`}>
      <RowLabel>{copy(row.label)}</RowLabel>
      <span className={styles.value}>
        {row.chips && <AccountChips chips={row.chips} {...chipHandlers} />}
        {row.value &&
          ('unknown' in row.value ? (
            <span className={styles.count}>{UNKNOWN}</span>
          ) : 'count' in row.value ? (
            <span className={styles.count}>
              {row.value.atLeast ? t('spaceCard.count.atLeast', { value: formatCount(row.value.count, locale) }) : formatCount(row.value.count, locale)}
            </span>
          ) : (
            <Typography variant="p2" color="secondary" component="span">
              {copy(row.value)}
            </Typography>
          ))}
        {row.action &&
          (row.action.primary ? (
            <CardButton action={row.action} onAct={onAct} testId="spaceCardPrimary" />
          ) : (
            <RowLink action={row.action} onAct={onAct} testId={`spaceCardRowAction-${row.kind}`} />
          ))}
      </span>
    </div>
  );
}

/**
 * `badge` is the Space's own badge (its initials or photo), which a social card replaces with its profile's network
 * mark; `onAct` runs every card action; `onManage` opens the Manage Online Presence window (`add`: with its add-link
 * field open; `bi`: the click's button name), absent when there is no window to open (the bindings read failed);
 * `onFooter` opens Connectors; `onChip` is a "Connected accounts" chip's click; `onSelectProfile` picks the profile a
 * social card shows.
 */
export function SpaceCard({
  card,
  badge,
  onAct,
  onManage,
  onFooter,
  onChip,
  onSelectProfile,
}: {
  card: SpaceCardModel;
  badge: ReactNode;
  onAct: (action: SpaceCardAction) => void;
  onManage?: (add: boolean, bi: string) => void;
  onFooter: () => void;
  onChip: (chip: SpaceCardChip) => void;
  onSelectProfile: (chip: SpaceCardChip) => void;
}) {
  const { t } = useTranslation();
  const copy = usePresenceCopy();
  const visits = card.rows.find((r) => r.kind === 'visits');
  const rows = card.rows.filter((r) => r.kind !== 'visits');
  const chipHandlers: ChipHandlers = {
    onChip,
    onSelect: onSelectProfile,
    onMore: () => onManage?.(false, 'space_card_chips_more'),
    onAdd: () => onManage?.(true, 'space_card_chips_add'),
  };
  const manage = card.manage && onManage;
  return (
    <Card component="section" variant="outline" disablePadding className={styles.card} data-testid="spaceCard" data-variant={card.variant}>
      {card.chips && <TopChips chips={card.chips} {...chipHandlers} />}
      <div className={styles.identity}>
        <div className={styles.head}>
          <div className={styles.who}>
            {card.glyph ? (
              <span className={styles.glyphBadge} aria-hidden>
                <PresenceGlyph glyph={card.glyph} size={28} />
              </span>
            ) : (
              badge
            )}
            <span className={styles.whoText}>
              <span className={styles.name}>{card.name}</span>
              {card.sub && (
                <Typography variant="p2" color="secondary" component="span" noWrap>
                  {copy(card.sub)}
                </Typography>
              )}
            </span>
          </div>
          {card.view && <ViewLink action={card.view} onAct={onAct} />}
        </div>
        {(card.buttons.length > 0 || manage) && (
          <div className={styles.actions}>
            {manage && (
              <Button
                variant="contained"
                color="standard"
                size="medium"
                className={styles.manage}
                data-testid="spaceCardManage"
                onClick={() => onManage(false, 'space_card_manage')}
              >
                {t('spaceCard.action.manage')}
              </Button>
            )}
            {card.buttons.map((a) => (
              <CardButton key={a.bi} action={a} onAct={onAct} testId={`spaceCardButton-${a.bi}`} />
            ))}
          </div>
        )}
      </div>
      {(visits || rows.length > 0) && (
        <div className={styles.details}>
          {visits && <VisitsStrip row={visits} onAct={onAct} />}
          {rows.map((row) =>
            row.kind === 'migrate' ? (
              <MigrateBlock key={row.kind} row={row} onAct={onAct} />
            ) : (
              <QuietRow key={row.kind} row={row} onAct={onAct} chipHandlers={chipHandlers} />
            ),
          )}
        </div>
      )}
      {card.footer && (
        <div className={styles.footer} data-testid="spaceCardFooter">
          <Typography variant="p2" color="secondary" component="span">
            {t('spaceCard.footer.lead')}{' '}
            <TextButton size="small" underline className={styles.link} data-testid="spaceCardFooterLink" onClick={onFooter}>
              {t('spaceCard.footer.link')}
            </TextButton>
          </Typography>
          <ArrowRight size={16} aria-hidden className={`${styles.footerArrow} dir-flip`} />
        </div>
      )}
    </Card>
  );
}
